/**
 * Pure helpers for the FHIR Implementation Guide (IG) domain (e.g. Clinical).
 *
 * An "iframe domain" group presents FHIR IGs instead of JSON schemas. A subgroup
 * fetch returns a `fhirIgs` list (`{ name, version, publishVersion }`); selecting
 * one resolves an `igUri` (a published HTML URL) that is shown in an iframe.
 *
 * These helpers hold no React state and perform no I/O so they can be unit
 * tested in isolation. Side effects (fetching artifacts/versions) live in
 * `hooks/useFhirIg` and `rtk/api/datagroupsApi`.
 */

const asArray = (value) => (Array.isArray(value) ? value : []);

export function normalizeFhirIg(item, parent = '') {
  if (typeof item === 'string') {
    return item
      ? { name: item, version: null, publishVersion: null, parent, subGroup: parent }
      : null;
  }
  if (!item || typeof item !== 'object' || !item.name) return null;
  // The IG's own `subGroup` is authoritative for building the artifacts path.
  // When the list is fetched via the aggregated "All" subgroup, `parent` (the
  // fetched path) is "All" but each IG belongs to a specific real subgroup, so
  // `subGroup` must win. Fall back to `parent`/`subGroup` as available.
  const subGroup = item.subGroup ?? item.parent ?? parent;
  return {
    name: item.name,
    version: item.version ?? null,
    publishVersion: item.publishVersion ?? null,
    parent: subGroup,
    subGroup,
    description: item.description ?? '',
  };
}

/**
 * Normalize a raw `fhirIgs` list, dropping malformed entries.
 *
 * @param {Array<object|string>} items
 * @param {string} [parent]
 * @returns {Array<object>}
 */
export function normalizeFhirIgs(items, parent = '') {
  return asArray(items)
    .map((item) => normalizeFhirIg(item, parent))
    .filter(Boolean);
}

/**
 * Find a FHIR IG by name within a normalized list. When `parent` is provided it
 * disambiguates IGs that share a name across subgroups, falling back to the
 * first name match.
 *
 * @param {Array<object>} items
 * @param {string} name
 * @param {string} [parent]
 * @returns {object|undefined}
 */
export function findFhirIg(items, name, parent = '') {
  const list = asArray(items).filter((ig) => ig?.name === name);
  if (list.length === 0) return undefined;
  if (parent) return list.find((ig) => ig.parent === parent) || list[0];
  return list[0];
}

/**
 * Extract the published HTML URL from an artifacts response.
 *
 * The artifacts endpoint returns `{ igUri, ... }`; `igUri` is the public HTML
 * URL to render in the iframe. Returns an empty string when absent so callers
 * can branch on truthiness without null checks.
 *
 * @param {object} artifacts
 * @returns {string}
 */
export function resolveIgUri(artifacts) {
  const uri = artifacts?.igUri;
  return typeof uri === 'string' ? uri.trim() : '';
}

/**
 * Extract the downloadable package URL from an artifacts response.
 *
 * The artifacts endpoint returns `{ igUri, packageUrl, ... }`; `packageUrl`
 * points to the guide's downloadable package (e.g. the FHIR/NPM package
 * tarball). Returns an empty string when absent so callers can branch on
 * truthiness without null checks.
 *
 * @param {object} artifacts
 * @returns {string}
 */
export function resolvePackageUrl(artifacts) {
  const uri = artifacts?.packageUrl;
  return typeof uri === 'string' ? uri.trim() : '';
}

/**
 * Derive a filename for a downloaded IG package from the artifacts metadata,
 * falling back to a generic name. Prefers the URL's own basename, then a
 * `<name>-<publishVersion>` composed from metadata.
 *
 * @param {object} artifacts
 * @param {string} packageUrl
 * @returns {string}
 */
export function packageFileName(artifacts, packageUrl) {
  let fromUrl = '';
  try {
    const { pathname } = new URL(packageUrl);
    const base = pathname.split('/').filter(Boolean).pop() || '';
    fromUrl = decodeURIComponent(base);
  } catch {
    fromUrl = '';
  }
  if (fromUrl) return fromUrl;

  const name = artifacts?.name || 'implementation-guide';
  const version = artifacts?.publishVersion || artifacts?.version;
  return version ? `${name}-${version}.tgz` : `${name}.tgz`;
}

/**
 * Whether an `igUri` is safe to load in an iframe. Only http(s) URLs are
 * allowed; this blocks `javascript:`/`data:` and other unexpected schemes from
 * an untrusted API response.
 *
 * @param {string} uri
 * @returns {boolean}
 */
export function isSafeIgUri(uri) {
  if (typeof uri !== 'string' || uri.trim() === '') return false;
  try {
    const { protocol } = new URL(uri);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

export function isExternalIgLink(rawHref, resolvedHref, appOrigin) {
  if (typeof rawHref === 'string' && rawHref.trim().startsWith('#')) return false;
  if (typeof resolvedHref !== 'string' || resolvedHref.trim() === '') return false;
  if (typeof appOrigin !== 'string' || appOrigin === '') return false;
  try {
    const { protocol, origin } = new URL(resolvedHref);
    if (protocol !== 'http:' && protocol !== 'https:') return false;
    return origin !== appOrigin;
  } catch {
    return false;
  }
}
